import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { Container, Grid, IconButton, Link, Typography } from '@mui/material';
import { Box } from '@mui/system';
import FacebookIcon from 'components/icons/facebook-icon';
import ForsiaEmailIcon from 'components/icons/forsia-email-icon';
import InstagramIcon from 'components/icons/instagram-icon';
import LinkedinIcon from 'components/icons/linkedin-icon';
import { useTranslation } from 'hooks/support/useTranslation';
import Image from 'next/legacy/image';
import { BrandContext } from 'pages/_app';
import { useContext, useEffect, useState } from 'react';
import { Brands } from 'utility/brands';
import ForsiaFacebookIcon from '../icons/forsia-facebook-icon';
import ForsiaInstagramIcon from '../icons/forsia-instagram-icon';
import ForsiaLinkedinIcon from '../icons/forsia-linkedin-icon';
import ForsiaPhoneIcon from '../icons/forsia-phone-icon';

const forsiaSpecificIcons = {
  linkedin: (height: number, width: number) => (
    <ForsiaLinkedinIcon height={height} width={width} />
  ),
  email: () => <ForsiaEmailIcon />,
  facebook: (height: number, width: number) => (
    <ForsiaFacebookIcon height={height} width={width} />
  ),
  instagram: (height: number, width: number) => (
    <ForsiaInstagramIcon height={height} width={width} />
  ),
  phone: () => <ForsiaPhoneIcon />,
};

const defaultIcons = {
  linkedin: (height: number, width: number) => (
    <LinkedinIcon height={height} width={width} />
  ),
  email: () => <MailOutlineOutlinedIcon fontSize="small" />,
  facebook: (height: number, width: number) => (
    <FacebookIcon height={height} width={width} />
  ),
  instagram: (height: number, width: number) => (
    <InstagramIcon height={height} width={width} />
  ),
  phone: () => <PhoneOutlinedIcon fontSize="small" />,
};

const Footer = () => {
  const brand = useContext(BrandContext);
  const { t } = useTranslation('footer');
  const [mobile, setMobile] = useState(false);

  const isForsia = brand.details.brandAbbreviation === Brands.FORSIA;

  const brandStyles = isForsia ? { bgcolor: 'primary.main' } : {};
  const brandIcons = isForsia ? forsiaSpecificIcons : defaultIcons;
  const brandLogo = isForsia
    ? '/img/forsia-footer-logo.svg'
    : brand.details.media.logo.imageUrl;

  useEffect(() => {
    setMobile(window.innerWidth < 900);
  }, []);

  return (
    <Box
      sx={{
        ml: 0,
        pt: { xs: 2, xl: 5 },
        mt: '40px',
        mr: -4,
        pr: 4,
        minHeight: { xl: 220 },
        height: '100%',
        backgroundImage: brand.details.media.footerIllustration
          ? `url(${brand.details.media.footerIllustration.imageUrl})`
          : 'none',
        backgroundPosition: 'right top',
        backgroundRepeat: 'no-repeat',
        ...brandStyles,
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          pt: 2,
        }}
      >
        <Grid className="u" container spacing={20} rowSpacing={3}>
          <Grid item xs={12} md={6} lg={4} xl={3} style={{ paddingTop: 0 }}>
            <Image
              src={brandLogo}
              width={mobile ? 190 : 300}
              height={mobile ? 63 : 105}
              alt="company logo"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4} xl={3}>
            <Typography color={'background.default'} variant="body2">
              {t('address')}
            </Typography>
            <Typography color={'background.default'} variant="body2">
              {t('cvr')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} lg={4} xl={3}>
            <Box sx={{ display: 'flex' }}>
              {brandIcons.phone()}
              <Typography
                color={'background.default'}
                variant="body2"
                sx={{ ml: 1, pb: '8px' }}
              >
                <Link
                  color="secondary"
                  underline="none"
                  href={`tel:${t('phoneNumber')}`}
                >
                  {t('phoneNumber')}
                </Link>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex' }}>
              {brandIcons.email()}
              <Typography variant="body2" sx={{ ml: 1 }}>
                <Link
                  color="secondary"
                  underline="none"
                  href={`mailto:${t('email')}`}
                >
                  {t('email')}
                </Link>
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} lg={12} xl={3}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: 150,
                pb: mobile ? '20px' : 0,
              }}
            >
              {brand.details.social.linkedin && (
                <IconButton
                  href={brand.details.social.linkedin}
                  target="_blank"
                >
                  {brandIcons.linkedin(36, 36)}
                </IconButton>
              )}
              {brand.details.social.facebook && (
                <IconButton
                  href={brand.details.social.facebook}
                  target="_blank"
                >
                  {brandIcons.facebook(36, 36)}
                </IconButton>
              )}
              {brand.details.social.instagram && (
                <IconButton
                  href={brand.details.social.instagram}
                  target="_blank"
                >
                  {brandIcons.instagram(36, 36)}
                </IconButton>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Footer;
